import React, { useEffect, useState } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'
import { useNavigate, useLocation } from 'react-router-dom'
import { client } from '../../utils/api-client'
import { makeNumbersFarsi, makeNumbersLatin } from 'utils/farsi'
import useTimer from 'hooks/useTimer'
import { Helmet } from 'react-helmet-async'

interface IFormInput {
  verificationCode: number | string
}

export const ForgotPasswordVerificationCodeForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { username } = location.state || {}
  const [timerFinish, setTimerFinish] = useState(false)
  const timer = useTimer({
    from: 122,
    to: 0,
    intervalSecond: 1,
    finished: (value) => setTimerFinish(true),
  })

  useEffect(() => {
    timer.start()
    if (timer.value === 0) timer.pause()
  }, [timer])

  const requestResendVerificationCode = async () => {
    const normalizedCellphone = makeNumbersLatin(
      String(username?.username ?? ''),
    )
    const data = {
      cellphone: normalizedCellphone,
      client_id: '',
    }
    timer.restart(122)
    await client('phones-verification-codes', { data })
  }

  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors },
  } = useForm<IFormInput>()
  const onSubmit: SubmitHandler<IFormInput> = async (options) => {
    const normalizedResetCode = makeNumbersLatin(
      String(options.verificationCode ?? ''),
    )
    let data = { resetCode: normalizedResetCode }
    const result = await client('user/reset-password-code/verify', { data })
    if (result) {
      navigate('/reset-password', {
        state: {
          resetCode: normalizedResetCode,
        },
      })
    }
  }

  React.useEffect(() => {
    setFocus('verificationCode')
  }, [setFocus])

  return (
    <div className='my-12'>
      <Helmet>
        <title className='text-xl font-semibold font-semibold'>کد تایید</title>
        <meta
          name='description'
          content='بوک استوری - هر کتاب یک داستان، هر داستان یک زندگی دوباره'
        />
      </Helmet>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className='max-w-lg mt-5 mx-auto flex flex-col my-5 h-auto border py-8 px-4 sm:px-6 rounded-md'
        style={{
          backgroundColor: 'var(--card-bg)',
          color: 'var(--text-primary)',
          borderColor: 'var(--border-color)',
        }}
      >
        <h1 className='text-bold text-xl text-right pb-2.5 mb-3'>
          ما یک کد تایید به شماره تلفن{' '}
          <span className='italic mx-1 font-bold tex-base font-bold'>
            {username.username}
          </span>
          ارسال کردیم. لطفا کد تایید خود را وارد نمایید:
        </h1>
        {timer.value === 0 ? (
          <p>
            <button
              className='mb-1 underline text-sm sm:text-base'
              onClick={() => requestResendVerificationCode()}
            >
              اگر هنوز کد فعال‌سازی را دریافت نکرده‌اید، برای ارسال دوباره کلیک
              کنید.
            </button>
          </p>
        ) : (
          <p className='mb-1 text-sm leading-relaxed'>
            اگر تا
            <span className='verification-code-resend-count-down-value mx-1'>
              {makeNumbersFarsi(timer.value)}
            </span>
            ثانیه دیگر پیامک ما را دریافت نکردید، می‌توانید ارسال دوباره آن را
            درخواست دهید.
          </p>
        )}
        <input
          className='input-theme block outline-none w-full rounded border py-3 px-4 mt-8 text-base sm:text-lg'
          {...register('verificationCode', { required: true, maxLength: 20 })}
          dir='ltr'
        />
        {errors.verificationCode && (
          <p className='text-[#bf1650] before:inline before:content-["⚠"]'>
            {' '}
            کد تایید الزامی است.
          </p>
        )}
        <input
          className='w-full rounded text-white uppercase border-none mt-8 sm:mt-10 py-3 sm:py-4 px-4 text-base sm:text-lg font-medium active:transition-all active:duration-[0.3s] active:opacity-80 cursor-pointer'
          style={{ backgroundColor: 'var(--accent-primary)' }}
          type='submit'
          value='ادامه'
        />
      </form>
    </div>
  )
}
